import React, { useEffect, useState } from "react";
import { withStyles } from "@material-ui/core/styles";
import { Grid, TextField, WithStyles } from "@material-ui/core";
import { styles } from "./EmptyOrderView.styles";
import Typography from "@material-ui/core/Typography/Typography";
import { useTranslation } from "react-i18next";
import { CenteredContainer } from "../../components/CenteredContainer/CenteredContainer";
import { useSnackbar } from "notistack";
import ToastStatusEnum from "../../../constants/ToastStatusEnum";
import { Breadcrumbs } from "../../components/Breadcrumbs/Breadcrumbs";
import { useHistory } from "react-router-dom";
import { AutocompleteUserDropdown } from "../../components/FormComponents/AutocompleteUserDropdown";
import { useDispatch, useSelector } from "react-redux";
import * as UserDetailsAction from "../../../stores/user/UserDetailsAction";
import UserDetailsModel from "../../../models/user/UserDetailsModel";
import { IUniversalTableData } from "../../components/UniversalTable/interfaces";
import SubscriptionModel from "../../../models/subscription/SubscriptionModel";
import { selectRequesting } from "../../../selectors/requesting/RequestingSelector";
import * as SubscriptionAction from "../../../stores/subscriptions/SubscriptionAction";
import { isPermissionGranted } from "../../../utilities/permissionUtils";
import { useGetRoles } from "../../../hooks/useGetRole";
import Autocomplete from "@material-ui/lab/Autocomplete/Autocomplete";
import { BlueButton } from "../../components/Buttons/BlueButton";
import { DateRangesTable } from "../../components/DateRangesTable";
import { consolidateDateToNumber } from "../../../utilities/dateTimeUtils";
import { useDispatchWithError } from "../../../hooks/useDispatchWithError";
import { UserDetails } from "../../components/UserDetails/UserDetails";
import { selectVisibleCountries } from "../../../selectors/countries/CountriesSelector";

export interface IAddMultipleSubscriptionsProps extends WithStyles<typeof styles> {
}

const AddMultipleSubscriptionsViewPure: React.FunctionComponent<IAddMultipleSubscriptionsProps> = ({classes}: IAddMultipleSubscriptionsProps) => {
    const {t} = useTranslation();

    const dispatch = useDispatch();

    const history = useHistory();
    const countries = useSelector(state => selectVisibleCountries(state));
    const {enqueueSnackbar} = useSnackbar();

    const roles = useGetRoles();

    useEffect(() => {
        dispatch(UserDetailsAction.requestAllUsersDetails());
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const allUsersFromState = useSelector(state => state.userDetails?.all);

    let allUsers;
    if (allUsersFromState) {
        allUsers = Object.values(allUsersFromState)?.map((user: UserDetailsModel) => ({
            userId: user.id,
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email
        }));
    };

    const [orderDetails, setOrderDetails] = React.useState({
        userId: ""
    });

    const handleUserChange = user => {
        setOrderDetails({
            ...orderDetails,
            userId: user?.userId
        });
    };

    useEffect(() => {
        if (orderDetails.userId) {
            dispatch(UserDetailsAction.requestUser(orderDetails.userId));
            dispatch(SubscriptionAction.requestAllUserDateRanges(orderDetails.userId));
            if (isPermissionGranted(roles, "subscriptions.users.write"))
                dispatch(SubscriptionAction.requestUserUnassignedSubscriptionTypes(orderDetails.userId));
            dispatch(UserDetailsAction.requestUser(orderDetails.userId));
        }
    }, [dispatch, orderDetails.userId, roles]);

    const userDetails = useSelector((state) => state.userDetails?.requestedUser);

    let [selectedSubscriptions, setSelectedSubscriptions] = useState([]);

    const userUnassignedSubscriptionTypesFromState = useSelector(
        state => state.subscriptions?.userUnassignedSubscriptionTypes
    );

    let addableSubscriptionTypes;
    if (userUnassignedSubscriptionTypesFromState) {
        addableSubscriptionTypes = Object.values(userUnassignedSubscriptionTypesFromState)?.map(
            (subscription: SubscriptionModel) => ({
                subscriptionTypeId: subscription.id,
                name: subscription.name,
                description: subscription.description,
                isFilterable: subscription.isFilterable,
                ...(userDetails?.userLocked && {
                    actionsDisabled: ["addSubscription"]
                })
            })
        );
    }

    const isRequestingDateRanges = useSelector(state =>
        selectRequesting(state, [SubscriptionAction.REQUEST_ALL_USER_DATE_RANGES_FINISHED])
    );

    const handleMultipleSubscriptionsSelection = (event, value) => {
        setSelectedSubscriptions(value);
    };

    const [tempDateRange, setTempDateRange] = useState<IUniversalTableData>([]);

    const handleEditDateRange = (index, newData) => {
        const updatedDateRange = {
            startDate: consolidateDateToNumber(newData.startDate),
            endDate: consolidateDateToNumber(newData.endDate),
            visibilityStartDate: consolidateDateToNumber(newData.visibilityStartDate),
            visibilityEndDate: consolidateDateToNumber(newData.visibilityEndDate),
            trial: newData.trial,
            salesTrial: false
        };
        setTempDateRange([updatedDateRange]);
    };

    const handleAddDateRange = newData => {
        const newDateRange = {
            startDate: consolidateDateToNumber(newData.startDate),
            endDate: consolidateDateToNumber(newData.endDate),            
            visibilityStartDate: consolidateDateToNumber(newData.visibilityStartDate),
            visibilityEndDate: consolidateDateToNumber(newData.visibilityEndDate),
            trial: newData.trial,
            salesTrial: false
        };
        setTempDateRange([newDateRange]);
    };

    const handleDeleteFinished = () => {
        setTempDateRange([]);
    };

    const [dispatchWithError, multipleSubscriptionsError] = useDispatchWithError(
        SubscriptionAction.CREATE_MULTIPLE_SUBSCRIPTIONS_FINISHED,
        () => {
            enqueueSnackbar("Multiple subscriptions were submitted.", {variant: ToastStatusEnum.Info});
            history.push("/admin/manage-users/add-multiple-subscriptions/success");
        }
    );

    const handleSubmit = () => {
        //filter the duplicates from the subscriptions list
        const subscriptionsNames = Object.values(selectedSubscriptions).map((subscription) => (subscription.name));
        const subscriptionsNamesUnique = [...new Set(subscriptionsNames)];

        let sendData;

            const restrictedSendData = {
                userId: orderDetails.userId,
                subscriptions: subscriptionsNamesUnique,
                ...(tempDateRange[0].startDate && {startDate: consolidateDateToNumber(tempDateRange[0].startDate)}),
                ...(tempDateRange[0].endDate && {endDate: consolidateDateToNumber(tempDateRange[0].endDate)}),
                ...(tempDateRange[0].visibilityStartDate && {visibilityStartDate: consolidateDateToNumber(tempDateRange[0].visibilityStartDate)}),
                ...(tempDateRange[0].visibilityEndDate && {visibilityEndDate: consolidateDateToNumber(tempDateRange[0].visibilityEndDate)}),
                trial:tempDateRange[0].trial === "true"
            }
            sendData = restrictedSendData;
        dispatchWithError(SubscriptionAction.createMultipleSubscriptions(sendData));
    };

    if (multipleSubscriptionsError) {
        enqueueSnackbar(multipleSubscriptionsError, {variant: ToastStatusEnum.Error});
    }

    return (
        <CenteredContainer>
            <Breadcrumbs
                items={[
                    {title: t("admin.adminPanel"), href: "/admin"},
                    {title: "Manage users", href: "/admin/manage-users"},
                    {
                        title: "Add multiple subscriptions"
                    }
                ]}
            />
            <div className={classes.title}>
                <Typography variant="h2">Add multiple subscriptions</Typography>
            </div>
            <Grid container spacing={5} direction="row">
                <Grid item xs={6}>
                    <Typography variant="h4">Select user from the list</Typography>
                    {allUsers && (
                        <AutocompleteUserDropdown
                            allUsers={allUsers}
                            selectedUserId={userDetails?.userId}
                            handleUserChange={handleUserChange}
                        />
                    )}
                </Grid>
            </Grid>
            {orderDetails.userId && userDetails && addableSubscriptionTypes && <div className={classes.topMargin}>
                <UserDetails
                    countries={countries}
                    userDetails={userDetails}
                ></UserDetails>

                <Typography variant="h4">
                    Select subscriptions from the list
                </Typography>
                <Autocomplete
                    classes={{
                        inputRoot: classes.aCInputRoot,
                        option: classes.aCOption,
                        listbox: classes.aCListbox
                    }}
                    id="multipleSubscriptionsSelect"
                    multiple
                    options={addableSubscriptionTypes}
                    getOptionLabel={(option: any) => option.name}
                    getOptionDisabled={(option: any) => option.isFilterable}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            placeholder="Select subscriptions"
                        />
                    )}
                    onChange={handleMultipleSubscriptionsSelection}
                />

                <Typography variant="h4" className={classes.bigTopMargin}>
                    Add date range
                </Typography>
                <DateRangesTable 
                    dateRanges={tempDateRange}
                    handleEditDateRange={handleEditDateRange}
                    handleAddDateRange={handleAddDateRange}
                    handleDeleteFinished={handleDeleteFinished}
                    isRequestingDateRanges={isRequestingDateRanges}
                    oneDateRange={true}
                />

                <BlueButton 
                    disabled={selectedSubscriptions.length === 0 || tempDateRange.length === 0}
                    onClick={handleSubmit}
                    >Submit
                </BlueButton>
                
            </div>}
        </CenteredContainer>
    );
};

export const ManageUsersAddMultipleSubscriptionsView = withStyles(styles)(AddMultipleSubscriptionsViewPure);
